import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast/useToast';
import { collection, query, where, limit, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ALBARAN_STATUS } from '../types/albaran';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';

const HistorialAlbaranes = () => {
  const { user, companyId, role, isCompanyAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const isAdmin = isCompanyAdmin?.() || isSuperAdmin?.() || role === 'company_admin' || role === 'super_admin';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todas');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  useEffect(() => {
    loadRequests();
  }, [user, companyId, role, statusFilter]);

  const loadRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      console.log('🔍 Cargando albaranes...');
      console.log('👤 User ID:', user.uid);
      console.log('🏢 Company ID:', companyId);

      // Admins ven todas las de la empresa, usuarios solo las suyas
      let q;
      if (isAdmin && companyId) {
        q = query(
          collection(db, 'albaran_requests'),
          where('companyId', '==', companyId),
          limit(100)
        );
      } else {
        q = query(
          collection(db, 'albaran_requests'),
          where('requesterId', '==', user.uid),
          limit(50)
        );
      }

      console.log('📊 Ejecutando query simplificada...');
      const querySnapshot = await getDocs(q);
      console.log('📋 Documentos encontrados:', querySnapshot.docs.length);
      
      let results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));

      // Ordenar en el cliente por fecha
      results.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime; // Más reciente primero
      });

      console.log('📄 Albaranes cargados:', results);

      // Filtrar por estado si no es "todas"
      if (statusFilter !== "todas") {
        results = results.filter(s => s.status === statusFilter);
        console.log('🔍 Después del filtro de estado:', results.length);
      }

      setRequests(results);

    } catch (err) {
      console.error('❌ Error loading albaran requests:', err);
      setError('Error al cargar el historial de albaranes');
      showToast({ 
        type: "error", 
        message: "Error cargando el historial de albaranes" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const getOrigenBadge = (origen) => {
    if (origen === 'kanban-cejm') {
      return {
        color: '#8b5cf6',
        text: '🔗 Kanban CEJM',
        icon: '🔗'
      };
    }
    return {
      color: '#6b7280',
      text: '📱 App interna',
      icon: '📱'
    };
  };

  const handleStatusChange = async (requestId, newStatus) => {
    if (!requestId || !newStatus) return;
    
    try {
      setUpdatingStatus(requestId);
      
      // Actualizar en Firestore
      const requestRef = doc(db, 'albaran_requests', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === ALBARAN_STATUS.PROCESADA && { processedAt: new Date() }),
        ...(newStatus === ALBARAN_STATUS.ENTREGADA && { deliveredAt: new Date() })
      });
      
      // Actualizar en el estado local
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: newStatus, updatedAt: new Date() }
            : request
        )
      );
      
      console.log(`Estado actualizado: ${requestId} -> ${newStatus}`);
      
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Error al actualizar el estado');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case ALBARAN_STATUS.ENVIADA:
        return ALBARAN_STATUS.PROCESADA;
      case ALBARAN_STATUS.PROCESADA:
        return ALBARAN_STATUS.ENTREGADA;
      default:
        return null;
    }
  };

  const getStatusButtonText = (currentStatus) => {
    switch (currentStatus) {
      case ALBARAN_STATUS.ENVIADA:
        return 'Marcar como Procesada';
      case ALBARAN_STATUS.PROCESADA:
        return 'Marcar como Entregada';
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      [ALBARAN_STATUS.ENVIADA]: '#f59e0b',
      [ALBARAN_STATUS.PROCESADA]: '#3b82f6',
      [ALBARAN_STATUS.ENTREGADA]: '#10b981',
      'pendiente': '#dc2626' // Rojo para solicitudes pendientes desde Kanban
    };
    
    const labels = {
      [ALBARAN_STATUS.ENVIADA]: 'Enviada',
      [ALBARAN_STATUS.PROCESADA]: 'Procesada',
      [ALBARAN_STATUS.ENTREGADA]: 'Entregada',
      'pendiente': 'Pendiente de gestionar'
    };

    return {
      color: badges[status] || '#6b7280',
      text: labels[status] || status
    };
  };

  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      request.numeroPedido?.toLowerCase().includes(searchLower) ||
      request.nombreDestinatario?.toLowerCase().includes(searchLower) ||
      request.numeroSerie?.toLowerCase().includes(searchLower) ||
      request.consignatario?.toLowerCase().includes(searchLower)
    );
  });

  const { paginated, page, pageSize, totalPages, totalItems, goTo, changePageSize } = usePagination(filteredRequests);

  const formatDate = (date) => {
    if (!date) return "Sin fecha";
    
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container">
        <p style={{ padding: 24, textAlign: 'center' }}>Cargando historial...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <h1 className="title">📦 Historial de Albaranes</h1>

      <div className="card grid">
        {/* Filtros y acciones */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="meta">Filtrar por estado:</span>
            <select 
              value={statusFilter} 
              onChange={(e) => handleStatusFilter(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="todas">Todas</option>
              <option value="pendiente">Pendiente de gestionar</option>
              <option value={ALBARAN_STATUS.ENVIADA}>Enviadas</option>
              <option value={ALBARAN_STATUS.PROCESADA}>Procesadas</option>
              <option value={ALBARAN_STATUS.ENTREGADA}>Entregadas</option>
            </select>
          </div>

          <button 
            className="btn" 
            onClick={() => navigate('/solicitar-albaran')}
          >
            Nueva Solicitud
          </button>
        </div>

        {/* Barra de búsqueda */}
        <div style={{ marginTop: 16 }}>
          <input
            type="text"
            placeholder="Buscar por pedido, destinatario, serie o consignatario..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ width: '100%', background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, color: '#111827', padding: '10px 12px', fontSize: '14px' }}
          />
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div style={{ 
            background: '#dc2626', 
            color: 'white', 
            padding: 12, 
            borderRadius: 8, 
            marginTop: 16 
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Lista de albaranes */}
        {filteredRequests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p className="meta">No hay solicitudes de albaranes que mostrar</p>
            <button 
              className="btn" 
              onClick={() => navigate('/solicitar-albaran')}
              style={{ marginTop: 12 }}
            >
              Crear primera solicitud
            </button>
          </div>
        ) : (
          <div className="grid">
            {paginated.map((request) => {
              const statusInfo = getStatusBadge(request.status);
              const origenInfo = getOrigenBadge(request.origen);
              return (
                <div 
                  key={request.id} 
                  className="doc-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/albaran/${request.id}`)}
                >
                  <div className="doc-card-header">
                    <h3 className="doc-card-title">
                      {request.numeroPedido} - {request.nombreDestinatario || request.personaContacto}
                    </h3>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {request.origen && (
                        <span 
                          className="badge"
                          style={{ 
                            backgroundColor: origenInfo.color,
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px'
                          }}
                          title={origenInfo.text}
                        >
                          {origenInfo.icon}
                        </span>
                      )}
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: statusInfo.color,
                          color: 'white',
                          fontSize: '11px'
                        }}
                      >
                        {statusInfo.text}
                      </span>
                    </div>
                  </div>

                  <div className="doc-card-body">
                    <div className="doc-card-row">
                      <span className="meta">Fecha solicitud:</span>
                      <span className="doc-card-value">{formatDate(request.fechaSolicitud || request.createdAt)}</span>
                    </div>

                    <div className="doc-card-row">
                      <span className="meta">Técnico solicitante:</span>
                      <span className="doc-card-value">{request.tecnicoSolicitante || request.tecnicoResponsable}</span>
                    </div>

                    <div className="doc-card-row">
                      <span className="meta">Técnico responsable:</span>
                      <span className="doc-card-value">{request.tecnicoResponsable}</span>
                    </div>

                    {request.numeroPresupuesto && (
                      <div className="doc-card-row">
                        <span className="meta">Nº Presupuesto:</span>
                        <span className="doc-card-value">{request.numeroPresupuesto}</span>
                      </div>
                    )}

                    <div className="doc-card-row">
                      <span className="meta">Nº Serie:</span>
                      <span className="doc-card-value" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {request.numeroSerie}
                      </span>
                    </div>

                    <div className="doc-card-row">
                      <span className="meta">Contacto:</span>
                      <span className="doc-card-value">{request.personaContacto}</span>
                    </div>

                    {request.telefonoContacto && (
                      <div className="doc-card-row">
                        <span className="meta">Teléfono:</span>
                        <span className="doc-card-value">{request.telefonoContacto}</span>
                      </div>
                    )}

                    {request.emailCliente && (
                      <div className="doc-card-row">
                        <span className="meta">Email cliente:</span>
                        <span className="doc-card-value">{request.emailCliente}</span>
                      </div>
                    )}

                    {request.tipoTransporte && (
                      <div className="doc-card-row">
                        <span className="meta">Transporte:</span>
                        <span className="doc-card-value">{request.tipoTransporte}</span>
                      </div>
                    )}

                    {request.condicionesEspeciales && (
                      <div className="doc-card-row">
                        <span className="meta">Condiciones especiales:</span>
                        <span className="doc-card-value" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                          {request.condicionesEspeciales}
                        </span>
                      </div>
                    )}

                    {request.observaciones && (
                      <div className="doc-card-row">
                        <span className="meta">Observaciones:</span>
                        <span className="doc-card-value" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                          {request.observaciones}
                        </span>
                      </div>
                    )}

                    {request.consignatario && (
                      <div className="doc-card-row">
                        <span className="meta">Consignatario:</span>
                        <span className="doc-card-value">{request.consignatario}</span>
                      </div>
                    )}

                    {request.direccionEntrega && (
                      <div className="doc-card-row">
                        <span className="meta">Dirección:</span>
                        <span className="doc-card-value">{request.direccionEntrega}</span>
                      </div>
                    )}
                  </div>

                  <div className="doc-card-actions">
                    <button 
                      className="copy"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/albaran/${request.id}`);
                      }}
                    >
                      Ver Detalles
                    </button>
                    
                    {/* 🔧 Botón para crear albarán desde solicitud */}
                    <button 
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/upload?from=solicitud&requestId=${request.id}&type=albaran`);
                      }}
                      style={{ 
                        backgroundColor: '#16a34a', 
                        borderColor: '#22c55e',
                        fontSize: '12px',
                        padding: '6px 12px'
                      }}
                    >
                      📦 Crear Albarán
                    </button>
                    
                    {getNextStatus(request.status) && (
                      <button 
                        className="btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(request.id, getNextStatus(request.status));
                        }}
                        disabled={updatingStatus === request.id}
                        style={{ 
                          backgroundColor: '#16a34a', 
                          borderColor: '#22c55e',
                          fontSize: '12px',
                          padding: '6px 12px'
                        }}
                      >
                        {updatingStatus === request.id ? (
                          '⏳ Actualizando...'
                        ) : (
                          getStatusButtonText(request.status)
                        )}
                      </button>
                    )}
                    
                    <button 
                      className="copy"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Funcionalidad de duplicar solicitud
                        const params = new URLSearchParams({
                          duplicate: request.id
                        });
                        navigate(`/solicitar-albaran?${params.toString()}`);
                      }}
                    >
                      Duplicar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Información adicional */}
        <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <p className="meta" style={{ margin: 0 }}>
            📊 Total de albaranes: {filteredRequests.length}
            {statusFilter !== 'todas' && ` (filtradas por: ${getStatusBadge(statusFilter).text})`}
            {isAdmin && <span style={{ marginLeft: 8, color: '#f59e0b' }}>— Vista de administrador</span>}
          </p>
          <Pagination page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems} goTo={goTo} changePageSize={changePageSize} />
        </div>
      </div>
    </div>
  );
};

export default HistorialAlbaranes;